<!--
  * About Page
  * User friendly explanatory text of application functionality.
  *
  * Copyright (c) 2020-2021 Dev Null Productions - All Rights Reserved
  -->
<template>
  <MainLayout section="about">
    <div id="about">
      <div id="about_content">
        <div id="about_section1" class="about_section">
          <h2>
            A Transaction Tracker for {{no_blockchain_configured ? 'The Blockchain' : configured_blockchain_upper}}
          </h2>

          <p>
            With <span class="zt">{{app_name}}</span>, monitor
            {{no_blockchain_configured ? 'distributed ledger' : configured_blockchain_upper}} transactions
            in real time and setup alerts to be notified activity via email, text message, and more.
          </p>


          <div class="horizontal">
            <p>
              By leveraging a rich and powerful pattern matching system, <span class="zt">{{app_name}}</span>
              facilitates the inclusion or exclusion of the exact transactions that you are interested in.
            </p>

            <img :src="require('./assets/about/about1.svg')" id="about1"/>
          </div>
        </div>

        <div id="about_section2" class="about_section">
          <h3>Filtering On Your Terms</h3>

          <p>
            If you are managing {{no_blockchain_configured ? 'a crypto' : 'an ' + configured_blockchain_upper}}
            wallet, or interested in Blockchain data for trading, auditing,
            or any other purpose, there is no better way to track it than with
            <span class="zt">{{app_name}}</span>.
          </p>

          <img :src="require('./assets/landing/' + active_blockchain + '/section3.png')" id="landing3" />

          <p>
            <span class="zt">{{app_name}}</span> not only allows you to filter transactions by <i>wallet</i>,
            <i>type</i>, and <i>currency</i>, but also to <b>deep inspect</b> every single transaction to
            only match certain criteria. Receive immediate alerts when any type of activity happens.
          </p>
        </div>

        <div id="about_section3" class="about_section">
          <h3>JSONPath Expressions</h3>

          <p>
            Transactions that are propagated across the Blockchain are expressed in JSON,
            a data format that allows users to specify the exact intent of the action they are executing.
          </p>

          <img :src="require('./assets/about/json.svg')" id="json" />

          <p>
            <span class="zt">{{app_name}}</span> uses a powerful expression matching technology known as
            <b>JSONPath</b> to allow you to specify the exact content of the JSON transactions that you would like to match.
          </p>

          <p>
            For example to only inspect {{active_blockchain_upper}}
            {{blockchain_jsonpath_expression_example1_explanation}},
            the following expression can be used:
          </p>

          <div id="expression_example1" class="expression_example">
            {{blockchain_jsonpath_expression_example1}}
          </div>

          <p>
            To specify <i>Payments</i> which transfer more than
            <i>500 {{active_blockchain_upper}}</i>, you can use:
          </p>

          <div id="expression_example2" class="expression_example">
            {{blockchain_jsonpath_expression_example2}}
          </div>

          <p id="expression_example2_explanation">
            {{blockchain_jsonpath_expression_example2_explanation}}
          </p>

          <p>
          Or to only inspect {{blockchain_jsonpath_expression_example3_explanation}}:
          </p>

          <div id="expression_example3" class="expression_example">
            {{blockchain_jsonpath_expression_example3}}
          </div>

          <p>
            But if you do not want to build these expressions, fear not!
            <span class="zt">{{app_name}}</span> bundles a <b>library of pre-built expressions</b>,
            allowing you to quickly setup the filters you are interested in.
            Each of these pre-built expressions are fully parameterized allowing you to plugin the
            transaction type, currencies, payment ranges that you are interested in and much more!
          </p>

          <img :src="require('./assets/landing/' + active_blockchain + '/section4a.png')" id="landing4a" />
        </div>

        <div id="about_section4" class="about_section">
          <h3>Programmatic Integration</h3>

          <p>
            With <span class="zt">{{app_name}}</span> you can receive alerts via email or text message,
            whichever you prefer.<br/><br/>
          </p>

          <div class="horizontal">
            <img :src="require('./assets/about/about2.svg')" id="about2" />

            <p>
            Advanced users can setup <b>webhooks</b> to be invoked on transaction activity.
            This means you can integrate the <span class="zt">{{app_name}}</span> notification system into any
            programatic environment including trading systems, auditing/forensics technologies, and much more!<br/><br/>
            </p>
          </div>

          <p>
            But this is just the tip of the iceberg, <router-link to="/txs">setup your first filter today!</router-link>
          </p>
        </div>

        <div id="about_section5" class="about_section">
          <div id="contact" />
          <h3>Contact Us</h3>

          <p style="text-align: center">
            Questions? <a href="/contact">Contact us today</a>!
          </p>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout  from './components/MainLayout'
import Blockchain  from './mixins/blockchain'
import config      from './config/config'

export default {
  name: 'About',

  mixins : [Blockchain],

  components: {
    MainLayout
  },

  methods : {
    pluralizer : function(num){
      return num == 1 ? '' : 's';
    }
  }
}
</script>

<style scoped>
#about{
  margin-bottom: 25px;
  padding: 25px;
}

#main_layout.sm #about,
#main_layout.xs #about{
  padding: unset;
  padding-top: 25px;
}

#about_content{
  border: 1px solid #ededed;
  border-radius: 5px;
  background-color: white;
  font-family: var(--theme-font1);
}

#about_content p{
  font-size: 1.7rem;
}

#main_layout.lg #about_content p,
#main_layout.md #about_content p{
  font-size: 1.2rem;
}

#main_layout.sm #about_content p,
#main_layout.xs #about_content p{
  font-size: unset;
}

#about_content h2,
#about_content h3{
  margin: 20px 0;
  text-align: center;
  font-family: var(--theme-font4);
}

.about_section{
  padding: 50px;
  color: var(--theme-color4);
}

#main_layout.sm .about_section,
#main_layout.xs .about_section{
  padding: 15px;
}

.about_section .horizontal{
  display: flex;
  flex-direction: row;
  align-items: center;
}

#main_layout.xs .horizontal{
  flex-direction: column;
}

#about_section1 h2{
  color: var(--theme-color1);
}

#about_section2 h3,
#about_section3 h3,
#about_section4 h3,
#about_section5 h3{
  color: black;
}

#about_section2,
#about_section3,
#about_section4,
#about_section5{
  border-top: 1px solid #ededed;
}

#about_section2 ul{
  list-style-type: square;
}

#about_section3 img{
  display: block;
  width: 75%;
  margin: auto;
}

#main_layout.sm #about_section3 img,
#main_layout.xs #about_section3 img{
  width: 100%;
}

#about1{
  width: 100%;
  max-width: 500px;
  flex-basis: 200%;
  background-color: var(--theme-color1);
  border-radius: 5px;
  margin: 10px;
}

#landing3{
  width: 100%;
  padding: 25px;
}

#json{
  max-width: 500px;
}

#about2{
  width: 100%;
  flex-basis: 200%;
}

#main_layout.sm #about1,
#main_layout.xs #about1{
  margin: unset;
}

#expression_example2{
  margin-bottom: 3px;
}

#expression_example2_explanation{
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 25px;
}

.zt{
  font-weight: bold;
  color: var(--theme-color2);
}

.expression_example{
  color: var(--theme-color1);
  margin: 25px;
  text-align: center;
}
</style>
